import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  OnDestroy,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";
import * as maplibregl from "maplibre-gl";

@Component({
  selector: "app-map-tracker",
  templateUrl: "./map-tracker.component.html",
  styleUrls: ["./map-tracker.component.css"],
})
export class MapTrackerComponent implements OnInit, OnChanges, OnDestroy {
  @Output() truckTrackDtlsEvent = new EventEmitter<string>();
  @Output() WOTrackDetailsEvent = new EventEmitter<string>();
  
  @Input() startLoc: any;
  @Input() endLoc: any;
  @Input() curLoc: any;
  @Input() routeSegments: any;

  map!: maplibregl.Map;
  marker: maplibregl.Marker | null = null;
  mapLoadedPromise: Promise<void> | null = null;
  isFirstLoad: boolean = true;
  
  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.curLoc && this.curLoc.length > 0) {
      this.navigateMaps();
    } else {
      this.centerMapToIndia();
    }
  }

  ngOnDestroy(): void {
    if (this.marker) {
      this.marker.remove();
    }
    if (this.map) {
      this.map.remove();
    }
  }

  async initialize() {
    if (this.mapLoadedPromise) return this.mapLoadedPromise;
    
    this.mapLoadedPromise = new Promise(async (resolve) => {
      let styleJson: any;
      try {
        styleJson = await this.http.get("/assets/map-styles/maptiler-streets-local.json").toPromise();
        if (styleJson.sources && styleJson.sources.openmaptiles) {
          styleJson.sources.openmaptiles.url = environment.tile_server_url;
        }
      } catch (e) {
        styleJson = {
          version: 8,
          sources: {},
          layers: [{ id: "bg", type: "background", paint: { "background-color": "#f2ead8" } }],
        };
      }

      const initialLat = Number(this.curLoc?.[0]?.lat || 20.5937);
      const initialLng = Number(this.curLoc?.[0]?.lng || 78.9629);

      this.map = new maplibregl.Map({
        container: "map-container",
        center: [initialLng, initialLat],
        zoom: this.curLoc && this.curLoc.length > 0 ? 6 : 4,
        minZoom: 2,
        maxZoom: 18,
        style: styleJson,
        transformRequest: (url, resourceType) => {
          if (url.includes(environment.tile_server_url)) {
            return { url: url, headers: { 'X-API-Key': environment.tile_api_key } };
          }
          return { url };
        }
      });

      this.map.addControl(new maplibregl.NavigationControl(), "top-right");
      this.map.addControl(new maplibregl.FullscreenControl(), "top-right");
      
      this.map.on('load', () => {
        resolve();
      });
    });

    return this.mapLoadedPromise;
  }

  public async centerMapToIndia() {
    await this.initialize();
    if (this.map) {
      this.map.flyTo({ center: [78.9629, 20.5937], zoom: 4 });
    }
    if (this.marker) {
      this.marker.remove();
      this.marker = null;
    }
  }

  public async navigateMaps() {
    if (!this.curLoc || this.curLoc.length === 0) return;
    await this.initialize();
    this.updatePolyline();
  }

  private getDistanceBetweenPoints(lat1: number, lng1: number, lat2: number, lng2: number) {
    const R = 6371e3;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; 
  }

  private filterSpikes(path: any[]) {
    if (!path || path.length === 0) return [];
    const filteredPath = [path[0]];
    const tolerance = 10;

    for (let i = 1; i < path.length; i++) {
      const location = path[i];
      const previousLocation = filteredPath[filteredPath.length - 1];

      const distance = this.getDistanceBetweenPoints(
        Number(previousLocation.lat), Number(previousLocation.lng),
        Number(location.lat), Number(location.lng)
      );

      if (distance < tolerance) continue;

      let isSpike = false;
      if (distance > 2000) { 
        if (i < path.length - 1) {
          const nextLocation = path[i + 1];
          const distanceNext = this.getDistanceBetweenPoints(
            Number(location.lat), Number(location.lng),
            Number(nextLocation.lat), Number(nextLocation.lng)
          );
          const distancePreviousToNext = this.getDistanceBetweenPoints(
            Number(previousLocation.lat), Number(previousLocation.lng),
            Number(nextLocation.lat), Number(nextLocation.lng)
          );
          if (distanceNext > 2000 && distancePreviousToNext < distance) {
            isSpike = true;
          }
        }
      }

      if (!isSpike) {
        filteredPath.push(location);
      }
    }
    return filteredPath;
  }

  updatePolyline() {
    if (!this.map || !this.curLoc || this.curLoc.length === 0) return;

    let solidFeatures: any[] = [];
    let dottedFeatures: any[] = [];

    if (this.routeSegments && this.routeSegments.length > 0) {
      const validSegments = this.routeSegments.filter((seg: any) => seg && seg.length > 1);

      for (const segCoords of validSegments) {
        const coords = segCoords.map((coord: any) => [Number(coord[0]), Number(coord[1])]);
        solidFeatures.push({ type: "Feature", geometry: { type: "LineString", coordinates: coords } });
      }

      for (let i = 0; i < validSegments.length - 1; i++) {
        const from = validSegments[i][validSegments[i].length - 1];
        const to = validSegments[i + 1][0];
        dottedFeatures.push({ type: "Feature", geometry: { type: "LineString", coordinates: [[Number(from[0]), Number(from[1])], [Number(to[0]), Number(to[1])]] } });
      }
    } else {
      const filteredLocs = this.filterSpikes(this.curLoc);
      if (filteredLocs.length === 0) return;

      const GAP_MS = 5 * 60 * 1000;
      let currentSegCoords = [[Number(filteredLocs[0].lng), Number(filteredLocs[0].lat)]];

      for (let i = 1; i < filteredLocs.length; i++) {
        const time1 = new Date(filteredLocs[i - 1].trackDateTime).getTime();
        const time2 = new Date(filteredLocs[i].trackDateTime).getTime();
        const gap = (time1 && time2) ? (time2 - time1) : 0;

        if (gap > GAP_MS) {
          if (currentSegCoords.length > 1) {
            solidFeatures.push({ type: "Feature", geometry: { type: "LineString", coordinates: currentSegCoords } });
          }
          dottedFeatures.push({ type: "Feature", geometry: { type: "LineString", coordinates: [[Number(filteredLocs[i - 1].lng), Number(filteredLocs[i - 1].lat)], [Number(filteredLocs[i].lng), Number(filteredLocs[i].lat)]] } });
          currentSegCoords = [[Number(filteredLocs[i].lng), Number(filteredLocs[i].lat)]];
        } else {
          currentSegCoords.push([Number(filteredLocs[i].lng), Number(filteredLocs[i].lat)]);
        }
      }

      if (currentSegCoords.length > 1) {
        solidFeatures.push({ type: "Feature", geometry: { type: "LineString", coordinates: currentSegCoords } });
      }
    }

    this.renderRouteLayers(solidFeatures, dottedFeatures);

    if (this.curLoc.length > 0) {
      const lastPoint = this.curLoc[this.curLoc.length - 1];
      const prevPoint = this.curLoc.length > 1 ? this.curLoc[this.curLoc.length - 2] : null;
      this.setMarker({ lat: Number(lastPoint.lat), lng: Number(lastPoint.lng) }, prevPoint);

      if (this.isFirstLoad) {
        let isSinglePoint = false;
        if (this.curLoc.length === 1 && (!this.routeSegments || this.routeSegments.length === 0)) {
          isSinglePoint = true;
        }

        const bounds = new maplibregl.LngLatBounds(
          [Number(this.curLoc[0].lng), Number(this.curLoc[0].lat)],
          [Number(this.curLoc[0].lng), Number(this.curLoc[0].lat)]
        );
        for (const pt of this.curLoc) {
          bounds.extend([Number(pt.lng), Number(pt.lat)]);
        }
        if (this.routeSegments) {
          for (const seg of this.routeSegments) {
            for (const pt of seg) {
              bounds.extend([Number(pt[0]), Number(pt[1])]);
            }
          }
        }
        
        // Log zoom level for debugging
        this.map.on('zoomend', () => {
          console.log('Current Map Zoom Level:', this.map.getZoom());
        });

        // Use a smaller maxZoom if it's just a single point
        this.map.fitBounds(bounds, { padding: 50, maxZoom: isSinglePoint ? 8 : 14 });
        this.isFirstLoad = false;
      }
    }
  }

  private renderRouteLayers(solidFeatures: any[], dottedFeatures: any[]) {
    const sourceSolid = this.map.getSource("route-solid") as maplibregl.GeoJSONSource;
    if (sourceSolid) {
      sourceSolid.setData({ type: "FeatureCollection", features: solidFeatures });
      (this.map.getSource("route-dotted") as maplibregl.GeoJSONSource).setData({ type: "FeatureCollection", features: dottedFeatures });
    } else {
      this.map.addSource("route-solid", { type: "geojson", data: { type: "FeatureCollection", features: solidFeatures } });
      this.map.addSource("route-dotted", { type: "geojson", data: { type: "FeatureCollection", features: dottedFeatures } });

      this.map.addLayer({ id: "route-line", type: "line", source: "route-solid", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#f97316", "line-width": 4 } });
      this.map.addLayer({ id: "route-gap", type: "line", source: "route-dotted", layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#94a3b8", "line-width": 3, "line-dasharray": [2, 4] } });
    }
  }

  private calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const dLon = ((lon2 - lon1) * Math.PI) / 180, lat1Rad = (lat1 * Math.PI) / 180, lat2Rad = (lat2 * Math.PI) / 180;
    const y = Math.sin(dLon) * Math.cos(lat2Rad), x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  }

  private setMarker(position: { lat: number; lng: number }, prevPosition?: any) {
    let bearing = 0;
    if (prevPosition) {
      bearing = this.calculateBearing(Number(prevPosition.lat), Number(prevPosition.lng), position.lat, position.lng);
    }

    if (this.marker) {
      this.marker.setLngLat([position.lng, position.lat]);
      const innerEl = this.marker.getElement().querySelector(".marker-icon-inner") as HTMLElement;
      if (innerEl && bearing !== 0) {
        innerEl.style.transform = `rotate(${bearing}deg)`;
      }
    } else {
      const svgIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 110"><path fill="#f97316" d="M16.0227 5.68833L15.6477 5.44674V6.77548H14.375L14.625 4.77688L14.375 4.61216L20.5796 0.0109854L21.0227 0.351404L16.0227 5.68833ZM28.9659 0.340425L29.4205 0L35.625 4.60118L35.375 4.7659L35.625 6.7645H34.3523V5.43576L33.9773 5.68833L28.9659 0.340425ZM36.8296 61.2429H13.1705V40.3893H36.8296V61.2429ZM37.6364 7.1159C37.6364 7.85165 36.8864 8.45563 35.9546 8.45563H14.7273C13.8068 8.45563 13.0568 7.85165 13.0568 7.1159C13.0568 6.36917 13.8068 5.7652 14.7273 5.7652H35.9546C36.8864 5.7652 37.6364 6.36917 37.6364 7.1159ZM43.6364 28.8699H27.3068H24H6.89775C6.89775 28.8699 7.54547 13.8914 8.62502 12.2222C9.70456 10.5531 13.375 6.87432 15.3182 6.63273C16.7614 6.45703 22.6818 6.54488 25.625 6.59978C28.4545 6.54488 34.1364 6.45703 35.5341 6.63273C37.3978 6.87432 40.9318 10.5531 41.9659 12.2222C43.0114 13.8914 43.6364 28.8699 43.6364 28.8699V28.8699ZM25.2614 19.393L9.55683 18.9538C9.55683 18.9538 15 8.89488 16.4318 8.19208C17.8523 7.47829 25.2614 7.74184 25.2614 7.74184V19.393ZM25.2614 7.74184C25.2614 7.74184 32.6818 7.47829 34.1023 8.19208C35.5227 8.90586 40.9773 18.9538 40.9773 18.9538L25.2614 19.393V7.74184ZM41.1023 28.6283C41.1023 29.419 40.0568 30.0559 38.7728 30.0559H11.7614C10.4659 30.0559 9.43182 29.419 9.43182 28.6283V19.426C9.43182 18.6353 10.4659 17.9984 11.7614 17.9984H38.7728C40.0568 17.9984 41.1023 18.6353 41.1023 19.426V28.6283ZM39.75 29.7814C39.75 30.572 38.7728 31.2089 37.5796 31.2089H12.5C11.3068 31.2089 10.3296 30.572 10.3296 29.7814V20.568C10.3296 19.7884 11.3068 19.1405 12.5 19.1405H37.5796C38.7728 19.1405 39.75 19.7884 39.75 20.568V29.7814ZM43.6364 26.7615V45.5615H6.89775V26.7615H6.9091C7.21591 23.7087 15.3182 21.2599 25.2614 21.2599C35.2159 21.2599 43.3068 23.7087 43.625 26.7615H43.6364ZM12.5796 43.53H9.43182V27.1459C9.70455 24.4884 16.7273 22.347 25.3637 22.347C33.3978 22.347 40.0341 24.2029 41.1364 26.6078C38.2159 25.4328 33.6477 24.6751 28.5114 24.6751C19.875 24.6751 12.8523 26.8054 12.5909 29.4739H12.5796V43.53ZM35.6023 36.5349C35.6023 37.3255 34.5568 37.9625 33.2727 37.9625H20.0568C18.7614 37.9625 17.7159 37.3255 17.7159 36.5349V31.78C17.7159 30.9893 18.7614 30.3524 20.0568 30.3524H33.2727C34.5568 30.3524 35.6023 30.9893 35.6023 31.78V36.5349ZM35.6023 37.1828C35.6023 37.9734 34.5568 38.6104 33.2727 38.6104H20.0568C18.7614 38.6104 17.7159 37.9734 17.7159 37.1828V32.4279C17.7159 31.6372 18.7614 31.0003 20.0568 31.0003H33.2727C34.5568 31.0003 35.6023 31.6372 35.6023 32.4279V37.1828ZM9.51139 20.2606L6.20456 18.5804L0.170455 18.8769L0 18.218L6.53409 18.0423L10.2614 19.2942L9.51139 20.2606ZM39.7387 19.3052L43.4659 18.0423L50 18.218L49.8295 18.8879L43.7955 18.5804L40.4886 20.2606L39.7387 19.3052ZM47.2046 108.375C47.2046 109.275 46.4773 110 45.5796 110H4.39775C3.50002 110 2.77274 109.275 2.77274 108.375V48.7571C2.77274 47.8567 3.50002 47.1319 4.39775 47.1319H45.5796C46.4773 47.1319 47.2046 47.8567 47.2046 48.7571V108.375ZM47.2046 48.7571V108.375C47.2046 109.275 46.4773 110 45.5796 110H37.8409C38.7386 110 39.4659 109.275 39.4659 108.375V48.7571C39.4659 47.8567 38.7386 47.1319 37.8409 47.1319H45.5796C46.4773 47.1319 47.2046 47.8567 47.2046 48.7571ZM13.6591 110H9.14773V47.1319H13.6591V110ZM32.6705 77.3086C32.6705 81.4046 29.2273 84.732 24.9887 84.732C20.75 84.732 17.3182 81.4046 17.3182 77.3086C17.3182 73.2125 20.75 69.8962 24.9887 69.8962C29.2273 69.8962 32.6705 73.2125 32.6705 77.3086ZM31.5 77.3086C31.5 80.7787 28.5796 83.6009 24.9887 83.6009C21.3977 83.6009 18.4887 80.7787 18.4887 77.3086C18.4887 73.8385 21.3977 71.0273 24.9887 71.0273C28.5796 71.0273 31.5 73.8385 31.5 77.3086ZM22.5568 80.2296C22.5568 80.4492 22.3636 80.6359 22.1364 80.6359C21.8977 80.6359 21.7045 80.4492 21.7045 80.2296V74.3326C21.7045 74.102 21.8977 73.9153 22.1364 73.9153C22.3636 73.9153 22.5568 74.102 22.5568 74.3326V80.2296ZM28.2386 80.2296C28.2386 80.4492 28.0455 80.6359 27.8182 80.6359C27.5796 80.6359 27.3977 80.4492 27.3977 80.2296V74.3217C27.3977 74.102 27.5796 73.9153 27.8182 73.9153C28.0455 73.9153 28.2386 74.102 28.2386 74.3217V80.2296Z" stroke="white" stroke-width="0.1" /></svg>`;
      const iconUrl = 'data:image/svg+xml;base64,' + btoa(svgIcon);

      const el = document.createElement("div");
      el.className = "maplibre-marker";
      el.style.width = "32px"; 
      el.style.height = "32px";
      el.style.cursor = "pointer";

      const innerEl = document.createElement("div");
      innerEl.className = "marker-icon-inner";
      innerEl.style.width = "100%";
      innerEl.style.height = "100%";
      innerEl.style.backgroundImage = `url(${iconUrl})`;
      innerEl.style.backgroundSize = "contain";
      innerEl.style.backgroundRepeat = "no-repeat";
      innerEl.style.backgroundPosition = "center";
      innerEl.style.transition = "transform 0.5s ease-in-out";
      
      el.appendChild(innerEl);

      this.marker = new maplibregl.Marker({ element: el })
        .setLngLat([position.lng, position.lat])
        .addTo(this.map);
        
      el.addEventListener("click", () => {
        this.truckTrackDtlsEvent.emit("truck_click");
      });
    }
  }
}

